import {
  Box,
  Dialog as ChakraDialog,
  DialogContentProps as ChakraDialogContentProps,
  DialogHeaderProps as ChakraDialogHeaderProps,
  DialogRootProps as ChakraDialogRootProps,
  Image,
  Stack,
  Text,
  useBreakpointValue,
  useDialogContext,
} from '@chakra-ui/react';
import * as React from 'react';

import { requestGuardedNavigation } from '#features/layout/model/guardedNavigation';
import { useDialogViewport } from '#features/ui/hooks/useDialogViewport';
import { useSheetDismiss } from '#features/ui/hooks/useSheetDismiss';
import {
  DialogInitialFocus,
  resolveDialogInitialFocus,
} from '#features/ui/utils/dialogInitialFocus';
import { CloseButton } from './CloseButton';
import { Portal } from './Portal';

/**
 * Телефон получает шторку снизу, десктоп остаётся центрированной модалкой.
 *
 * Причина не в моде: на телефоне центрированное окно с высоким содержимым оставляет поля
 * по краям и уводит действия в середину экрана, куда большой палец не дотягивается. Шторка
 * приезжает снизу, оттуда же, где лежит рука. Для нативной сборки это тем более важно.
 */
export const useIsDialogSheet = () => useBreakpointValue({ base: true, md: false }) ?? false;

/**
 * Обработчик захвата шторки — из содержимого в шапку.
 *
 * Через контекст, а не пропсом: шапку ставит вызывающий, все восемнадцать модалок, и просить
 * каждую пробросить обработчик значило бы, что забытый проброс тихо оставит шапку неподвижной.
 * `null` здесь честно значит «это не шторка» — на десктопе и при `sheetOnMobile={false}` хвататься
 * не за что.
 */
const SheetGripContext = React.createContext<((event: React.PointerEvent) => void) | null>(null);

/** Стили области захвата: без них жест не доживает до обработчика на телефоне. */
const GRIP_CSS = {
  // Иначе браузер забирает вертикальное движение себе под прокрутку.
  touchAction: 'none',
  // Иначе на iOS вместо шторки резинкой тянется страница.
  overscrollBehavior: 'contain',
} as const;

type DialogContentProps = ChakraDialogContentProps & {
  portalled?: boolean;
  portalRef?: React.RefObject<HTMLElement>;
  backdrop?: boolean;
  /** Отключить поведение шторки, если модалка мелкая и на телефоне уместна по центру. */
  sheetOnMobile?: boolean;
  /** Position a typing sheet inside the area above the on-screen keyboard. */
  fitVisualViewport?: boolean;
  ref?: React.RefObject<HTMLDivElement | null>;
};

export const DialogContent = function DialogContent({ ref, ...props }: DialogContentProps) {
  const {
    children,
    portalled = true,
    portalRef,
    backdrop = true,
    sheetOnMobile = true,
    fitVisualViewport = false,
    ...rest
  } = props;
  const isSheet = useIsDialogSheet() && sheetOnMobile;
  const dialog = useDialogContext();
  const viewport = useDialogViewport(isSheet && fitVisualViewport && dialog.open);
  // Свой ref нужен жесту, а вызывающий про это не знает и вправе передать собственный — поэтому
  // узел кладём в оба.
  const contentRef = React.useRef<HTMLDivElement | null>(null);
  const attachContent = React.useCallback(
    (node: HTMLDivElement | null) => {
      contentRef.current = node;
      if (ref) {
        ref.current = node;
      }
    },
    [ref]
  );
  const onGripPointerDown = useSheetDismiss({
    ref: contentRef,
    enabled: isSheet,
    onDismiss: () => dialog.setOpen(false),
  });
  // Дефолт применяем, только если вызывающий вообще не задал maxH. Проверка именно на
  // rest.maxH === undefined, а не просто спред до {...rest}: JSX-спред копирует и явный
  // undefined, поэтому maxH={cond ? 'min(90dvh, 600px)' : undefined} затирал бы дефолт и
  // оставлял шторку без ограничения высоты — на телефоне она вылезала бы за экран.
  const sheetMaxH = isSheet && rest.maxH === undefined ? { maxH: '90dvh' } : undefined;

  return (
    <Portal portalled={portalled} portalRef={portalRef}>
      {/* Затемнение и размытие живут здесь, а не в каждой модалке. Раньше вызывающие ставили
          рядом свой <Dialog.Backdrop/>, он оказывался вне портала, и его position:fixed ломался
          трансформом родителя (у блока поста есть motion-трансформ): подложка накрывала только
          кусок экрана и оставляла светлую полосу над попапом. Эта завёрнута в портал и потому
          всегда во весь экран. */}
      {backdrop && <ChakraDialog.Backdrop backdropFilter="blur(4px)" bg="blackAlpha.500" />}
      <ChakraDialog.Positioner
        style={
          viewport
            ? ({
                top: viewport.top,
                height: viewport.height,
                bottom: 'auto',
                '--dialog-viewport-height': `${viewport.height}px`,
              } as React.CSSProperties)
            : undefined
        }
      >
        <ChakraDialog.Content
          ref={attachContent}
          // Единственное исключение из геометрии шторки: maxH — это дефолт, который вызывающий
          // может перебить (см. sheetMaxH выше). Модалка с полем ввода считает высоту сама
          // по visualViewport, чтобы низ шторки с кнопками не оказался под клавиатурой, — а
          // жёсткие 90dvh такой расчёт затирали. dvh, а не vh, потому что vh на iOS не
          // вычитает адресную строку и низ шторки уезжает под неё. На клавиатуру не реагирует
          // ни vh, ни dvh: она не меняет layout viewport, поэтому там и нужен visualViewport.
          {...sheetMaxH}
          {...rest}
          {...(isSheet && fitVisualViewport
            ? { maxH: 'calc(var(--dialog-viewport-height, 100dvh) * 0.9)' }
            : {})}
          // Остальная геометрия шторки идёт ПОСЛЕ пропсов вызывающего намеренно. Половина модалок
          // задаёт свои maxW, w и borderRadius под десктоп, и если бы они побеждали, на телефоне
          // получалась бы шторка со скруглением со всех сторон и не на всю ширину. Отказаться
          // от шторки целиком можно через sheetOnMobile={false}, это предсказуемее, чем
          // угадывать, какой из пропсов вызывающего относился к десктопу.
          {...(isSheet
            ? {
                w: '100%',
                maxW: '100%',
                m: 0,
                borderRadius: 0,
                borderTopRadius: 'l3',
                // Домашний индикатор на телефонах без кнопки перекрывает нижние 34px.
                pb: 'env(safe-area-inset-bottom)',
              }
            : {})}
          asChild={false}
        >
          {/* Полоска-ручка: без неё шторка читается как окно, которое приехало не туда,
              а не как то, что можно утянуть вниз. За неё же шторку и тянут — область захвата
              делает всю ширину и высоту пальца, потому что четыре пикселя полоски пальцем
              не поймать. */}
          {isSheet && (
            <Box
              data-testid="dialog-sheet-grip"
              flexShrink={0}
              pt={2}
              pb={1}
              display="flex"
              justifyContent="center"
              userSelect="none"
              onPointerDown={onGripPointerDown}
              css={GRIP_CSS}
            >
              <Box
                w="40px"
                h="4px"
                borderRadius="full"
                // fg.subtle, а не border.emphasized: такого токена в теме нет, переменная
                // не резолвилась, и ручка была невидимой.
                bg="fg.subtle"
              />
            </Box>
          )}
          <SheetGripContext.Provider value={isSheet ? onGripPointerDown : null}>
            {children}
          </SheetGripContext.Provider>
        </ChakraDialog.Content>
      </ChakraDialog.Positioner>
    </Portal>
  );
};

/*
  `trapFocus` закрыт типом нарочно.

  Флаг `trapFocus={false}` выпускает фокус из открытого окна: клавиатура ходит по странице
  позади, а на телефоне клавиатура И ЕСТЬ фокус - поле внутри окна просто не поднимается.
  Флаг копировали из окна в окно с 2021 года; 20.09.2026 его сняли с девяти окон разом, и в тот
  же день он приехал обратно в двух новых - с кодом, скопированным у соседа.

  Тип честнее нового прохода в `pnpm lint`: попытка передать флаг падает на `pnpm type-check`
  сразу. Окну, которое и правда управляет фокусом само, придётся снять запрет здесь и объяснить
  почему - это и есть нужное поведение.
*/
/*
  `initialFocusEl` тоже закрыт своим типом, и по той же причине, что `trapFocus` выше.

  Тип zag (`MaybeElement = Nullable<HTMLElement>`) разрешает `null`, а focus-trap на `null`
  бросает исключение - "was specified but was not a node". Окна писали `() => null` в смысле
  "не фокусируй ничего", копировали это друг у друга, и при закрытии окна, открытого поверх,
  ошибка улетала в `errorComponent` роута: вместо страницы человек видел "We couldn't load this
  page". Подробности и цена - в шапке `resolveDialogInitialFocus`.

  Здесь `null` невозможен: либо своя функция, возвращающая узел, либо строка `'body'` -
  "фокус самому окну". Рантайм всё равно страхует, потому что `() => ref.current` у
  несмонтированного узла даёт `null`, и тип об этом не знает.
*/
type DialogRootProps = Omit<
  ChakraDialogRootProps,
  'placement' | 'motionPreset' | 'trapFocus' | 'initialFocusEl'
> & {
  placement?: ChakraDialogRootProps['placement'];
  motionPreset?: ChakraDialogRootProps['motionPreset'];
  initialFocusEl?: DialogInitialFocus;
};

/**
 * Обёртка над Dialog.Root с адаптивными умолчаниями. Вызывающему остаётся передать open и
 * onOpenChange; placement и анимацию можно переопределить, если модалка особенная.
 */
export const DialogRoot = ({
  placement,
  motionPreset,
  initialFocusEl,
  ids,
  open,
  defaultOpen = false,
  onOpenChange,
  ...props
}: DialogRootProps) => {
  const isSheet = useIsDialogSheet();
  const [localOpen, setLocalOpen] = React.useState(defaultOpen);
  // Own content boundary: a picker or confirmation must not discard a draft underneath it.
  const generatedContentId = React.useId();
  const contentId = ids?.content ?? generatedContentId;

  return (
    <ChakraDialog.Root
      placement={placement ?? (isSheet ? 'bottom' : 'center')}
      motionPreset={motionPreset ?? (isSheet ? 'slide-in-bottom' : 'scale')}
      scrollBehavior="inside"
      ids={{ ...ids, content: contentId }}
      initialFocusEl={resolveDialogInitialFocus(initialFocusEl, contentId)}
      {...props}
      open={open ?? localOpen}
      onOpenChange={(details) => {
        const change = () => {
          setLocalOpen(details.open);
          onOpenChange?.(details);
        };
        const content = document.getElementById(contentId);
        if (!details.open && content) requestGuardedNavigation(change, content);
        else change();
      }}
    />
  );
};

/**
 * Снять фокус с триггера после закрытия окна.
 *
 * Закрываясь, диалог возвращает фокус туда, откуда его открыли, — и кнопка остаётся
 * подсвеченной, будто её всё ещё держат. Для мыши это выглядит как залипший элемент:
 * человек уже вернулся к списку, а одна из кнопок горит.
 *
 * Возврат происходит НЕ синхронно с `onOpenChange`, а после размонтирования содержимого,
 * поэтому blur вызывается через два кадра: снятый раньше возврата, он ничего не даёт —
 * ровно эта ошибка и осталась в первой версии правки.
 */
export const releaseDialogFocus = () => {
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      const active = document.activeElement;
      if (active instanceof HTMLElement) {
        active.blur();
      }
    });
  });
};

export const DialogCloseTrigger = function DialogCloseTrigger({
  ref,
  ...props
}: ChakraDialog.CloseTriggerProps & { ref?: React.RefObject<HTMLButtonElement | null> }) {
  return (
    <ChakraDialog.CloseTrigger
      position="absolute"
      top="2"
      insetEnd="2"
      {...props}
      asChild
      tabIndex={-1}
    >
      <CloseButton size="md" ref={ref}>
        {props.children}
      </CloseButton>
    </ChakraDialog.CloseTrigger>
  );
};

/**
 * Квадратная иконка слева от заголовка: логотип игры или сервиса. Рамка и внутренний
 * отступ нужны потому, что иконки приходят с непредсказуемым фоном — без них светлый
 * логотип сливается с поверхностью окна, а тёмный выглядит дырой.
 */
export const DialogHeaderIcon = ({ src, alt }: { src: string; alt?: string }) => (
  <Box
    borderRadius="l3"
    overflow="hidden"
    boxSize={10}
    flexShrink={0}
    p={1}
    border="1px solid"
    borderColor="border.subtle"
  >
    <Image src={src} alt={alt ?? ''} w="100%" h="100%" objectFit="contain" />
  </Box>
);

/**
 * Фирменный градиент Discord-модалок. Раньше им заливали всю шапку — теперь это
 * двухпиксельная линия под ней: айдентика читается, а окно не разваливается на
 * цветную плашку и всё остальное.
 */
// Сам градиент лежит в теме (`semantic-tokens/colors.ts`, `accent.brand`): значения живут там,
// здесь остаётся имя. Раньше фиолетовый конец был ступенью палитры, а красный - числом.
export const DIALOG_ACCENT_BRAND = 'var(--chakra-colors-accent-brand)';

type DialogHeaderProps = Omit<ChakraDialogHeaderProps, 'title'> & {
  title: React.ReactNode;
  /** Иконка слева — обычно <DialogHeaderIcon/>, но подойдёт и аватар. */
  icon?: React.ReactNode;
  /** Вторая строка: контекст, который не влезает в заголовок. */
  description?: React.ReactNode;
  /**
   * Красит разделитель под шапкой вместо волосяного. Единственная уступка модалкам
   * со своей айдентикой (Discord) — раньше они заливали градиентом всю шапку.
   */
  accent?: string;
  /** Крестик рисует сама шапка. Выключать, если модалка закрывается только кнопкой. */
  closable?: boolean;
};

/**
 * Единственная шапка на все модалки: иконка, заголовок, подпись, крестик.
 * Геометрия и разделитель заданы в слот-рецепте dialog, здесь только состав.
 */
export const DialogHeader = ({
  title,
  icon,
  description,
  accent,
  closable = true,
  children,
  ...rest
}: DialogHeaderProps) => {
  // Шапка — вторая область захвата после ручки: за полоску в четыре пикселя целиться неудобно,
  // а тело шторки отдавать жесту нельзя, там прокручиваются длинные формы. Крестик внутри
  // продолжает нажиматься: жест не отменяет действие по умолчанию, а короткое касание не
  // проходит порог смещения.
  const onGripPointerDown = React.useContext(SheetGripContext);

  return (
    <ChakraDialog.Header
      {...(accent ? { borderColor: 'transparent' } : {})}
      {...(onGripPointerDown
        ? { onPointerDown: onGripPointerDown, userSelect: 'none' as const, css: GRIP_CSS }
        : {})}
      {...rest}
    >
      {/* Акцент лежит поверх прозрачной границы, а не вместо неё: так шапка не меняет
        высоту от того, брендированная модалка или обычная. */}
      {accent ? <Box position="absolute" insetX={0} bottom="-1px" h="2px" bg={accent} /> : null}
      {icon}
      <Stack gap={0.5} minW={0} flex="1">
        <ChakraDialog.Title lineHeight="short">{title}</ChakraDialog.Title>
        {description ? (
          // as="div": в подписи встречаются не только строки, но и ряды чипов, а <p> их не
          // выдерживает по вложенности.
          <Text as="div" textStyle="body" color="fg.secondary">
            {description}
          </Text>
        ) : null}
      </Stack>
      {children}
      {closable ? (
        // top: 50% вместо подобранного вручную отступа — крестик встаёт по центру шапки
        // и с иконкой (72px), и без неё (62px).
        <DialogCloseTrigger top="50%" insetEnd={3} transform="translateY(-50%)" />
      ) : null}
    </ChakraDialog.Header>
  );
};

export const DialogFooter = ChakraDialog.Footer;
export const DialogBody = ChakraDialog.Body;
export const DialogBackdrop = ChakraDialog.Backdrop;
export const DialogTitle = ChakraDialog.Title;
export const DialogDescription = ChakraDialog.Description;
export const DialogTrigger = ChakraDialog.Trigger;
export const DialogActionTrigger = ChakraDialog.ActionTrigger;
